import React, { useState } from 'react';
import {
  HeartHandshake,
  FileText,
  UploadCloud,
  MessageSquare,
  Clock,
  CheckCircle2,
  Lock,
  PlusCircle,
  LogOut,
} from 'lucide-react';
import Logo from '@/components/Logo';

export default function BeneficiaryPortal() {
  const [activeTab, setActiveTab] = useState('requests');
  const [requests, setRequests] = useState([
    {
      id: 'AID-9042',
      category: 'Housing Assistance',
      requested_amount: '$1,200.00',
      status: 'Under Review',
      submitted_at: '2026-08-01',
      case_worker: 'Sarah Jenkins (Outreach Lead)',
    },
  ]);

  const [showModal, setShowModal] = useState(false);
  const [category, setCategory] = useState('Emergency Housing');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const newReq = {
      id: `AID-${Math.floor(1000 + Math.random() * 9000)}`,
      category,
      requested_amount: `$${amount || '0.00'}`,
      status: 'Submitted',
      submitted_at: new Date().toISOString().split('T')[0],
      case_worker: 'Assigned upon triage',
    };
    setRequests([newReq, ...requests]);
    setShowModal(false);
    setAmount('');
    setDescription('');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col">
      {/* Header Bar */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <Logo height={26} />
          <span className="text-xs bg-rose-100 text-rose-700 font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5">
            <Lock size={12} /> Confidential Aid Portal
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right text-xs">
            <div className="font-semibold text-slate-800">Jane Applicant</div>
            <div className="text-slate-500">Applicant ID: APP-8821</div>
          </div>
          <button
            onClick={() => (window.location.href = '/login')}
            className="text-slate-500 hover:text-slate-800 text-xs font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 transition"
          >
            <LogOut size={14} /> Exit Portal
          </button>
        </div>
      </header>

      {/* Primary Layout */}
      <main className="max-w-5xl mx-auto w-full px-6 py-8 flex-1">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl p-6 shadow-md mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Community Aid & Support Portal</h1>
            <p className="text-blue-100 text-sm mt-1">
              Submit assistance requests, track status updates, and securely upload documents in confidence.
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="bg-white text-blue-700 font-semibold text-sm px-4 py-2.5 rounded-xl shadow hover:bg-blue-50 transition flex items-center gap-2"
          >
            <PlusCircle size={16} /> New Aid Request
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-slate-200 mb-6">
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-3 px-4 text-sm font-semibold border-b-2 flex items-center gap-2 transition ${
              activeTab === 'requests'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <HeartHandshake size={16} /> My Aid Requests
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`pb-3 px-4 text-sm font-semibold border-b-2 flex items-center gap-2 transition ${
              activeTab === 'documents'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <FileText size={16} /> Required Documents
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`pb-3 px-4 text-sm font-semibold border-b-2 flex items-center gap-2 transition ${
              activeTab === 'messages'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <MessageSquare size={16} /> Case Worker Messages
          </button>
        </div>

        {/* Content Views */}
        {activeTab === 'requests' && (
          <div className="space-y-4">
            {requests.map((req) => (
              <div
                key={req.id}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:border-slate-300 transition"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="text-xs text-slate-400 font-mono font-medium">{req.id}</div>
                    <h3 className="text-base font-bold text-slate-800 mt-0.5">{req.category}</h3>
                  </div>
                  <span
                    className={`text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5 ${
                      req.status === 'Submitted'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    <Clock size={12} /> {req.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs text-slate-600 pt-3 border-t border-slate-100">
                  <div>
                    <span className="text-slate-400 block text-[11px]">Requested Funding</span>
                    <span className="font-semibold text-slate-800 text-sm">{req.requested_amount}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Submitted Date</span>
                    <span className="font-medium text-slate-700">{req.submitted_at}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Assigned Case Worker</span>
                    <span className="font-medium text-slate-700">{req.case_worker}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 text-center shadow-sm">
            <UploadCloud size={40} className="mx-auto text-blue-500 mb-3" />
            <h3 className="text-base font-bold text-slate-800">Upload Supporting Documents</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto mt-1">
              Please upload proof of income, utility statements, or photo ID requested by your case manager.
            </p>
            <div className="mt-5 border-2 border-dashed border-slate-200 rounded-xl p-8 bg-slate-50 hover:bg-slate-100 transition cursor-pointer">
              <span className="text-xs font-semibold text-blue-600">Click to select files</span>
              <span className="text-xs text-slate-400 block mt-1">PDF, PNG, JPG up to 10MB</span>
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-base font-bold text-slate-800 mb-4">Secure Case Messaging</h3>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-600 mb-4">
              <div className="font-semibold text-slate-800 mb-1">Case Worker: Sarah Jenkins</div>
              <p>Hello Jane, we have received your request for housing assistance. Please upload your latest utility statement so we can finalize processing.</p>
              <span className="text-[10px] text-slate-400 block mt-2">Aug 2, 2026 at 2:15 PM</span>
            </div>
          </div>
        )}
      </main>

      {/* Modal for New Aid Request */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl border border-slate-200">
            <h2 className="text-lg font-bold text-slate-900 mb-1">Submit Emergency Aid Request</h2>
            <p className="text-xs text-slate-500 mb-4">
              All details submitted are strictly confidential and shared only with assigned assistance leads.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Assistance Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg p-2.5 bg-white text-slate-800 font-medium"
                >
                  <option value="Emergency Housing">Emergency Housing</option>
                  <option value="Food & Supplies">Food & Essential Supplies</option>
                  <option value="Medical & Health">Medical & Health Assistance</option>
                  <option value="Utility Relief">Utility Relief</option>
                </select>
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Requested Amount ($)</label>
                <input
                  type="number"
                  placeholder="e.g. 500"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  className="w-full border border-slate-300 rounded-lg p-2.5 text-slate-800"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Brief Description of Need</label>
                <textarea
                  rows={3}
                  placeholder="Describe your current situation and how aid will be applied..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  className="w-full border border-slate-300 rounded-lg p-2.5 text-slate-800"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 shadow"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
